'use client';

import React, { HTMLAttributes, useEffect, useRef, useState, type JSX } from 'react';
import Image from 'next/image';

import { Locale } from '../../../i18n.config';
import SendToFriendForm from '../SendToFriendForm';
import EnquiryForm from '../EnquiryForm';
import { Loading } from '../Loading';
import XIcon from '../Icons/X';
import useLocalStorage from '@/hooks/useLocalStorage';
import { IExperience } from '@/types/Experience';
import { IVilla } from '@/types/Villa';
import { ShortlistFooter } from './ShortlistFooter';

import { ISendToFriendsFormDictionary } from '@/dictionaries/components/SendToFriendsForm/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import {
  IHomeShortlistModal,
  IModalsDictionary,
} from '@/dictionaries/components/modals/types';

interface IShortListBodyProps extends HTMLAttributes<HTMLDivElement> {
  dict: IHomeShortlistModal;
  lang: Locale;
  sendToFriendFormDict: ISendToFriendsFormDictionary;
  enquiryFormDict: IEnquiryFormDictionary;
  countriesDict: ICountriesDictionary;
  datePickerDict: IDatePickerDictonary;
  modalsDict: IModalsDictionary;
  villasShortListModalContent: JSX.Element;
  experiencesShortListModalContent: JSX.Element;
  hasVillas: boolean;
  hasExperience: boolean;
  closeModal?: () => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ShortListBody: React.FC<IShortListBodyProps> = ({
  dict,
  lang,
  hasVillas,
  hasExperience,
  villasShortListModalContent,
  experiencesShortListModalContent,
  sendToFriendFormDict,
  closeModal = () => {},
  isLoading,
  setIsLoading,
}) => {
  const [selectedTab, setSelectedTab] = useState<'villas' | 'experiences'>(
    'villas',
  );
  const [currentPage, setCurrentPage] = useState<
    'default' | 'sendToFriend' | 'enquiry'
  >('default');

  const header = useRef<HTMLDivElement>(null);

  const [villaShortList, setVillaShortList] = useLocalStorage(
    'villas',
    [] as IVilla[],
  );

  const [experienceShortList, setExperienceShortList] = useLocalStorage(
    'experience',
    [] as IExperience[],
  );

  const skipTabLoading = useRef(true);

  useEffect(() => {
    if (skipTabLoading.current) {
      skipTabLoading.current = false;
      return;
    }

    setIsLoading(true);

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [selectedTab, setIsLoading]);

  useEffect(() => {
    if (header.current) {
      const observer = new window.IntersectionObserver(
        ([event]) => {
          event.target.classList.toggle(
            'bg-white',
            event.intersectionRatio < 1,
          );
          event.target.classList.toggle(
            'is-pinned',
            event.intersectionRatio < 1,
          );
          event.target.classList.toggle(
            'shadow-xl',
            event.intersectionRatio < 1,
          );
        },
        { threshold: [1] },
      );

      observer.observe(header.current);

      const unobserve = () => {
        if (header.current) {
          observer.unobserve(header.current);
        }
      };

      return () => {
        unobserve();
      };
    }
    //eslint-disable-next-line
  }, [header.current]);

  const handleClearShortList = () => {
    setVillaShortList([]);
    setExperienceShortList([]);
  };

  return (
    (currentPage === 'enquiry' && (
      <div className="relative flex flex-col h-[100vh] w-full py-0 sm:py-[50px] overflow-auto">
        <button
          onClick={() => closeModal()}
          className="w-10 h-10 absolute top-4 right-3 text-gray-900 sm:text-gray-500 z-50 group-[.is-pinned]:text-gray-900">
          <XIcon className="w-6 h-6 m-auto fill-current z-50" />
        </button>
        <EnquiryForm
          onCancel={() => setCurrentPage('default')}
          isModal
          isForShortlist
          media={[]}
          description=""
          bannerContent={<></>}
          villageId={null}
          experienceId={null}
        />
      </div>
    )) ||
    (currentPage === 'sendToFriend' && (
      <div className="overflow-auto h-[100vh] tablet:overflow-y-auto">
        <button
          onClick={() => closeModal()}
          className="w-10 h-10 absolute top-4 right-3 text-gray-900 tablet:text-gray-500 z-50 group-[.is-pinned]:text-gray-900">
          <XIcon className="w-6 h-6 m-auto fill-current z-50" />
        </button>
        <SendToFriendForm
          lang={lang}
          formDict={sendToFriendFormDict}
          onCancel={() => setCurrentPage('default')}
          isModal
        />
      </div>
    )) ||
    (currentPage === 'default' && (
      <div className="relative flex flex-col h-full w-full overflow-auto">
        <div
          ref={header}
          className="
          w-full group flex justify-between items-center flex-col py-5 px-4 tablet:px-10 desktop:px-20 mx-auto
          sticky -top-1 mt-5 sm:mt-[50px] tablet:flex-row mb-5
          group-[is-pinned]:bg-white z-40 transition duration-300 pointer-events-auto
        ">
          <div className="flex flex-col sm:flex-row justify-between items-center mx-auto w-full tablet:w-10/12">
            <div>
              <h2 className="text-3xl font-bold text-white z-10 flex flex-row group-[.is-pinned]:text-gray-900 font-title items-center space-x-2 tablet:text-3xl desktop:text-title">
                <Image
                  src={`https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/stars/fill.svg`}
                  alt="Shortlist"
                  width={18}
                  height={18}
                  className="tablet:h-7 tablet:w-auto"
                />
                <span>{dict.yourShortList}</span>
              </h2>
            </div>

            <div className="flex flex-row space-x-5 text-base sm:text-2xl font-bold font-title text-zinc-400">
              <button
                onClick={() => {
                  setSelectedTab('villas');
                }}
                className="bg-transparent">
                <span
                  style={{ textDecorationSkipInk: 'none' }}
                  className={
                    selectedTab === 'villas'
                      ? 'underline decoration-2 tablet:decoration-4 underline-offset-0 decoration-underline group-[.is-pinned]:text-gray-900 text-white'
                      : ''
                  }>
                  {dict.villas}
                </span>
              </button>
              <button
                onClick={() => {
                  setSelectedTab('experiences');
                }}
                className="bg-transparent">
                <span
                  style={{ textDecorationSkipInk: 'none' }}
                  className={
                    selectedTab === 'experiences'
                      ? 'underline decoration-2 tablet:decoration-4 underline-offset-0 decoration-underline group-[.is-pinned]:text-gray-900 text-white'
                      : ''
                  }>
                  {dict.experiences}
                </span>
              </button>
            </div>

            <button
              onClick={() => closeModal()}
              className="w-10 h-10 fixed top-[30px] desktop:top-6 sm:top-[20px] right-5 text-white z-50 group-[.is-pinned]:text-gray-900">
              <XIcon className="w-6 h-6 m-auto fill-current z-50" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 w-full max-w-default px-4 tablet:px-10 desktop:px-20 mx-auto tablet:pb-[50px]">
          <ul className="space-y-5 mx-auto w-full tablet:w-10/12">
            {isLoading ? (
              <Loading className="mx-auto mt-24" />
            ) : selectedTab === 'villas' ? (
              villasShortListModalContent
            ) : hasExperience ? (
              experiencesShortListModalContent
            ) : (
              <div className="flex flex-col gap-2">
                <h2 className="w-full text-center font-title text-[22px] font-semibold mt-16 text-white">
                  {dict.emptyExperiences}
                </h2>
                <button
                  className="w-full text-center text-white"
                  onClick={() => {
                    setIsLoading(true);
                    setSelectedTab('villas');
                  }}>
                  {dict.emptyExperiencesSubtitle}
                </button>
              </div>
            )}
          </ul>
        </div>

        {hasVillas && selectedTab === 'villas' && !isLoading && (
          <ShortlistFooter
            dict={dict}
            setCurrentPage={setCurrentPage}
            handleClearShortList={handleClearShortList}
          />
        )}
        {hasExperience && selectedTab === 'experiences' && !isLoading && (
          <ShortlistFooter
            dict={dict}
            setCurrentPage={setCurrentPage}
            handleClearShortList={handleClearShortList}
          />
        )}
      </div>
    ))
  );
};
